/**
 * T137 + T225 — DuplicateWarning dialog.
 *
 * Renders a modal when the backend rejects a create with 409 + code
 * `duplicate_detected` + `existingExpenseId`. Shows a deep-link to the
 * existing expense and a "Trotzdem speichern" button that re-submits
 * with `?ignoreDuplicate=true`.
 */
import { Link } from '@tanstack/react-router';
import { Button, SURFACE } from '../ui';

export interface DuplicateInfo {
  message: string;
  existingExpenseId: string;
}

export interface DuplicateWarningProps {
  info: DuplicateInfo | null;
  onForceSave: () => void;
  onCancel: () => void;
  submitting?: boolean;
}

export function DuplicateWarning({
  info,
  onForceSave,
  onCancel,
  submitting = false,
}: DuplicateWarningProps) {
  if (!info) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="duplicate-warning-title"
      data-testid="duplicate-warning-dialog"
    >
      <div className={`max-w-lg w-full rounded-lg p-6 space-y-4 ${SURFACE.solid}`}>
        <div className="flex items-start gap-3">
          <span className="text-3xl leading-none" aria-hidden="true">
            ⚠️
          </span>
          <div>
            <h2
              id="duplicate-warning-title"
              className="text-xl font-light text-white"
            >
              Möglicherweise ein Duplikat
            </h2>
            <p className="text-white/70 mt-1 text-sm whitespace-pre-wrap">
              {info.message}
            </p>
          </div>
        </div>

        <div className="p-3 rounded border border-white/10 bg-white/5 text-sm">
          <div className="text-white/50">Bereits vorhandener Beleg:</div>
          <Link
            to="/expenses/$id"
            params={{ id: info.existingExpenseId }}
            className="text-blue-300 hover:text-blue-200 underline font-mono"
            data-testid="duplicate-warning-existing-link"
            target="_blank"
            rel="noreferrer"
          >
            {info.existingExpenseId.slice(0, 8)}… ↗ (in neuem Tab öffnen)
          </Link>
        </div>

        <div className="text-xs text-white/40">
          Wenn du sicher bist, dass dies kein Duplikat ist (z. B. unterschiedliche
          Belegnummern vom gleichen Lieferanten), kannst du trotzdem speichern.
          Der Beleg wird dann zweimal im System geführt.
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="duplicate-warning-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={onForceSave}
            disabled={submitting}
            data-testid="duplicate-warning-force-save"
          >
            {submitting ? 'Speichern…' : 'Trotzdem speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Extract DuplicateInfo from a caught error, if the backend reported a
 * duplicate collision. Returns null otherwise.
 */
export function parseDuplicateError(err: unknown): DuplicateInfo | null {
  if (!err || typeof err !== 'object') return null;
  const anyErr = err as {
    status?: number;
    data?: { code?: string; existingExpenseId?: string; message?: string };
  };
  if (anyErr.status !== 409) return null;
  const data = anyErr.data;
  if (!data || data.code !== 'duplicate_detected' || !data.existingExpenseId) {
    return null;
  }
  return {
    message: data.message ?? 'Ein identischer Beleg existiert bereits.',
    existingExpenseId: data.existingExpenseId,
  };
}
